import { useQuery } from '@tanstack/react-query';
import SessionsService, {
  IDeviceSessionResponse,
} from '../services/device-service';
import { useConfig } from 'hooks/use-config';
import {CurrentUser} from "/state/store/common/useCommonStore";
/**
 * Custom hook to fetch the device sessions for the user.
 * It retrieves the sessions from the SessionsService using pagination.
 * The query is enabled only when the user's account ID is available.
 *
 * @param user - current logged user
 * @param {number} page - The current page of sessions to fetch (default is 0).
 * @param {number} pageSize - The number of sessions per page (default is 10).
 *
 * @returns {Object} The query result containing device sessions data.
 *
 * @example
 * const { data, isLoading, error } = useGetSessions(0, 10);
 */
export const useGetSessions = (user: CurrentUser | null, page = 0, pageSize = 10) => {
  const { configurationSettings } = useConfig();
  
  return useQuery<IDeviceSessionResponse>({
    queryKey: ['sessions', user?.id, page, pageSize],
    queryFn: async () => {
      return await SessionsService.getSessions({
        config: configurationSettings,
        page,
        pageSize,
        filter: {
          userId: user?.id ?? '',
        },
      });
    },
    enabled: !!user?.id,
  });
};

/**
 * Custom hook to fetch the active device sessions for the user.
 * It retrieves the active sessions from the SessionsService for the logged-in user.
 * The query is enabled only when the user's account ID is available.
 *
 * @returns {Object} The query result containing active device session data.
 *
 * @example
 * const { data, isLoading, error } = useGetActiveDeviceSessions();
 */

export const useGetActiveDeviceSessions = (user: CurrentUser | null) => {
  const { configurationSettings } = useConfig();
  return useQuery({
    queryKey: ['activeSessions', user?.id],
    queryFn: () => SessionsService.getActiveDeviceSessions(user?.id ?? '', configurationSettings),
    enabled: !!user?.id,
  });
};
